import React from "react";
import PropTypes from "prop-types";
import styled, { css, withTheme } from "styled-components";
import DatePicker from "react-datepicker";
import { DateTime } from "luxon";
import Calendar from "components/icons/calendar";
import "react-datepicker/dist/react-datepicker.css";
import visuallyHidden from "theme/visually-hidden";
import formatFieldErrors from "lib/format-field-errors";
import FieldError from "components/FieldError";
import { monthAbrv } from "lib/date-helpers";
import PillDropdown from "fields/PillDropdown";

const CurrentMonthName = styled.div`
  font-size: ${({ theme }) => theme.fontSize(2)};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderContainer = styled.div`
  border-bottom: 0.0625rem solid ${({ theme }) => theme.themeColors.secondary};
  display: flex;
  justify-content: space-between;
  padding: 0.625rem 0.875rem 1.125rem;
`;

const MonthNavigationButton = styled.button`
  background-color: transparent;
  border: none;
  height: 1.25rem;
  margin: 0;
  padding: 0;
  transition: all 0.3s;
  width: 1.25rem;

  svg {
    fill: ${({ theme }) => theme.themeColors.secondary};
    transition: ${({ theme }) => theme.opacityTransition};
  }

  :hover {
    cursor: pointer;
    svg {
      opacity: 0.8;
    }
  }

  :focus {
    outline: 0;
  }

  :focus-visible {
    outline: 0.125rem solid #0070f3;
  }
`;

const StyledCalendar = styled(Calendar)`
  vertical-align: middle;
  height: 1.25rem;
  pointer-events: none;
  z-index: 1;
  transition: ${({ theme }) => theme.opacityTransition};

  ${({ iconPosition }) =>
    iconPosition === "right" &&
    css`
      right: 0;
      color: ${({ theme }) => theme.themeColors.darkGreen3};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      color: ${({ theme }) => theme.themeColors.darkGreen3};
    `}
`;

const MaterialLabelWrapper = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize(2)};
  color: ${({ theme }) => theme.themeColors.secondary};
  line-height: 1.125rem;
  pointer-events: none;
  position: absolute;
  top: 0.75rem;
  transition: all 0.2s;

  ${({ showRequiredAsterisk, theme }) =>
    showRequiredAsterisk &&
    css`
      :after {
        content: "*";
        color: ${theme.themeColors.primary};
        font-size: 16px;
        line-height: 5px;
        position: absolute;
        right: -0.625rem;
        top: 50%;
      }
    `}

  ${({ hideLabelForPlaceholder }) =>
    hideLabelForPlaceholder &&
    css`
      ${visuallyHidden};
    `}

  ${({ hasIcon, iconPosition }) =>
    hasIcon &&
    iconPosition === "left" &&
    css`
      transform: translateX(2.1875rem);
    `}

  ${({ isActive }) =>
    isActive &&
    css`
      font-size: ${({ theme }) => theme.fontSize(1)};
      transform: translate(0, -1.5625rem);
    `}
`;

const StyledDatePicker = styled(DatePicker)`
  background-color: transparent;
  border: none;
  border-bottom: 0.0625rem solid ${({ theme }) => theme.themeColors.secondary};
  box-sizing: border-box;
  color: ${({ theme }) => theme.themeColors.darkGreen1};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.fontSize(2)};
  height: 2rem;
  padding: 0.3125rem;
  position: relative;
  transition: all 0.3s;

  ::placeholder {
    color: ${({ theme }) => theme.themeColors.darkGreen3};
  }

  ${({ hasIcon, iconPosition }) =>
    hasIcon &&
    iconPosition === "left" &&
    css`
      padding-left: 2rem;
    `}

  ${({ hasIcon, iconPosition }) =>
    hasIcon &&
    iconPosition === "right" &&
    css`
      padding-right: 2rem;
    `}

    ${({ error }) =>
    error &&
    css`
      border-color: ${({ theme }) => theme.themeColors.orange2};
    `}

  :focus {
    border-color: ${({ theme }) => theme.themeColors.primary};
    outline: none;
  }

  :disabled {
    background-color: transparent;
    opacity: 0.5;
  }
`;

const Wrapper = styled.div`
  line-height: 0;
  font-weight: 600;
  margin: 0;
  position: relative;
  border: 0.0625rem solid #8f978c;
  border-radius: 3.125rem;
  /* Outer Wrapper */
  .react-datepicker-wrapper {
    width: 100%;
  }

  /* Dropdown Calendar Wrapper Class */
  .cxl-datepicker {
    border: 0.0625rem solid ${({ theme }) => theme.themeColors.borderColor};
    border-radius: 0.5rem;
    box-shadow: 0rem 0.125rem 0.375rem rgba(138, 168, 181, 0.1253);
    font-family: ${({ theme }) => theme.fontFamily};
    min-width: 20rem;
    width: 100%;

    /* Tooltip Triangle */
    .react-datepicker__triangle {
      display: none;
    }

    /* Calendar Header Styles */
    .react-datepicker__header {
      background-color: transparent;
      border-bottom: none;
      margin: 0;
    }

    /* Month Name in Header */
    .react-datepicker__current-month {
      font-size: ${({ theme }) => theme.fontSize(2)};
    }

    /* Week Row */
    .react-datepicker__week {
      display: flex;
      justify-content: space-between;
    }

    /* Weekday Names Container */
    .react-datepicker__day-names {
      font-size: ${({ theme }) => theme.fontSize(0)};
      font-weight: ${({ theme }) => theme.fontWeights.bold};
      color: ${({ theme }) => theme.themeColors.goodfriendDark};
      margin-top: 0.125rem;
      display: flex;
      justify-content: space-between;
    }

    /* Individual Weekday Name inside Weekday Names Container */
    .react-datepicker__day-name {
      margin: 0.3125rem 0.5313rem;
      width: 1.75rem;

      /* :first-child {
        margin-left: 0;
      }

      :last-child {
        margin-right: 0;
      } */
    }

    /* Calendar Day Styles */
    .react-datepicker__day {
      border-radius: 50%;
      font-size: ${({ theme }) => theme.fontSize(1)};
      height: 1.75rem;
      line-height: 1.875rem;
      margin: 0.3125rem 0.5313rem;
      vertical-align: middle;
      width: 1.75rem;

      :first-child {
        margin-left: 0;
      }

      :last-child {
        margin-right: 0;
      }

      :hover {
        background-color: ${({ theme }) => theme.themeColorShades.secondary300};
      }
      transition: ${({ theme }) => theme.opacityTransition};
    }

    /* Selected/Active Calendar Day */
    .react-datepicker__day--selected {
      background-color: ${({ theme }) => theme.themeColors.primary};
      color: white;
    }

    /* Current Day */
    .react-datepicker__day--today :not(.react-datepicker__day--selected) {
      color: ${({ theme }) => theme.themeColors.primary};
      background-color: ${({ theme }) => theme.themeColors.white};
      :focus-visible {
        outline: none;
      }
    }

    /* Keyboard Selected Calendar Day */
    .react-datepicker__day--keyboard-selected {
      background-color: ${({ theme }) => theme.themeColorShades.secondary300};
    }

    /* Days from prev & next months in current view */
    .react-datepicker__day--outside-month {
      color: ${({ theme }) => theme.themeColors.secondary};
    }

    /* Month Container */
    .react-datepicker__month-container {
      float: none;
      margin: 0 auto;
    }

    /* Inner Month Wrapper (inside month-container) */
    .react-datepicker__month {
      margin-bottom: 1.25rem;
    }
  }

  /* Calendar Dropdown */
  /* Popper.js Wrapper */
  .react-datepicker-popper {
    width: 100%;
    z-index: 3;
  }

  /* Time Styles ---------------------------- */
  /* Container */
  .react-datepicker__input-time-container {
    border-top: 0.0625rem solid ${({ theme }) => theme.themeColors.secondary};
    margin: 0;
    padding: 0.625rem 0.875rem;
  }
`;

const BackArrowIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 -9.53674e-07C4.47715 -1.4365e-06 1.4365e-06 4.47715 9.53674e-07 10C4.70851e-07 15.5228 4.47715 20 10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 -4.70851e-07 10 -9.53674e-07ZM10.124 12.9795C10.4364 13.2919 10.9429 13.2919 11.2553 12.9795C11.5678 12.6671 11.5678 12.1606 11.2553 11.8481L9.40723 10L11.2553 8.15192C11.5678 7.8395 11.5678 7.33297 11.2553 7.02055C10.9429 6.70813 10.4364 6.70813 10.124 7.02055L7.71017 9.43434L7.14449 10L7.71017 10.5657L10.124 12.9795Z"
      />
    </g>
    <defs>
      <clipPath id="clip0">
        <rect width="20" height="20" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const NextArrowIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20ZM9.87603 7.0205C9.56361 6.70808 9.05708 6.70808 8.74466 7.0205C8.43224 7.33292 8.43224 7.83945 8.74466 8.15187L10.5928 9.99998L8.74466 11.8481C8.43224 12.1605 8.43224 12.667 8.74466 12.9795C9.05708 13.2919 9.56361 13.2919 9.87603 12.9795L12.2898 10.5657L12.8555 9.99998L12.2898 9.43429L9.87603 7.0205Z"
    />
  </svg>
);

const createHourList = () => {
  const hours = [];

  const currentDay = DateTime.local(2000, 1).startOf("day");

  // Get 15 min intervals for Business Hours dropdowns
  for (let i = 0; i < 96; i += 1) {
    const availableTime = currentDay.plus({ hours: i / 4 });
    const availableTimeInHours = availableTime.toLocaleString(
      DateTime.TIME_SIMPLE
    );
    const availableTimeIn24Hours = availableTime.toLocaleString(
      DateTime.TIME_24_SIMPLE
    );
    hours.push({
      value: availableTimeIn24Hours,
      label: availableTimeInHours,
    });
  }
  return hours;
};

const hoursOptions = createHourList();

const formatSelectValue = (val) => {
  const formattedValue = hoursOptions.find(
    (valToSearch) => valToSearch?.value === val
  );
  return formattedValue;
};

const CustomTimeInput = ({ date, value, onChange }) => {
  return (
    <PillDropdown
      options={hoursOptions}
      value={formatSelectValue(value)}
      onChange={(e) => {
        onChange(e?.value);
      }}
    />
  );
};

const StyledButton = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
`;

const IconContainer = styled.div`
  margin-right: 0.5rem;
`;

const ButtonText = styled.div`
// this margin top ensures this text is centered in the button
// sorry I know this isnt exactly optimal
  margin-top: 0.2rem;
  line-height: 1;
  font-size: 1rem;
  font-weight: 400;
  color: ${({ theme }) => theme.themeColors.darkGreen1};
`;

const CustomInput = React.forwardRef(({ value, onClick }, ref) => {
  return (
    <StyledButton onClick={onClick} ref={ref}>
      <IconContainer>
        <StyledCalendar />
      </IconContainer>
      <ButtonText>Date</ButtonText>
    </StyledButton>
  );
});

function DatePickerComponent({
  className,
  disabled,
  hasIcon,
  iconPosition,
  input: { onChange, value, ...input },
  isRequired,
  label,
  meta: { touched, active, submitError, error: syncError } = {},
  placeholder,
  showRequiredAsterisk,
  showTimeDropdown,
  theme,
  ...props
}) {
  const errors = formatFieldErrors({
    submitError,
    syncError,
  });
  const hasError = touched && errors && errors.length > 0;
  const errorProps = hasError ? { invalid: true } : {};

  return (
    <Wrapper className={className}>
      <MaterialLabelWrapper
        hasIcon={hasIcon}
        iconPosition={iconPosition}
        isActive={active || value || value?.length}
        hideLabelForPlaceholder={!!placeholder}
        showRequiredAsterisk={showRequiredAsterisk}
      >
        {label}
      </MaterialLabelWrapper>
      <StyledDatePicker
        customInput={<CustomInput />}
        customTimeInput={showTimeDropdown ? <CustomTimeInput /> : null}
        dateFormat={showTimeDropdown ? "MM/dd/yyyy h:mm aa" : "MM/dd/yyyy"}
        disabled={disabled}
        error={hasError || undefined}
        hasIcon={hasIcon}
        iconPosition={iconPosition}
        calendarClassName="cxl-datepicker"
        formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 1)}
        onChange={onChange}
        placeholderText={placeholder}
        selected={value}
        showPopperArrow={false}
        showRequiredAsterisk={showRequiredAsterisk}
        showTimeInput={showTimeDropdown}
        renderCustomHeader={({ decreaseMonth, increaseMonth, monthDate }) => {
          return (
            <>
              <HeaderContainer>
                <MonthNavigationButton onClick={decreaseMonth} type="button">
                  <BackArrowIcon />
                </MonthNavigationButton>
                <CurrentMonthName>
                  {monthAbrv[monthDate.getMonth()]}
                </CurrentMonthName>
                <MonthNavigationButton onClick={increaseMonth} type="button">
                  <NextArrowIcon />
                </MonthNavigationButton>
              </HeaderContainer>
            </>
          );
        }}
        {...props}
        {...errorProps}
        {...input}
      />
      <FieldError show={hasError} errors={errors} />
    </Wrapper>
  );
}

export default withTheme(DatePickerComponent);

DatePickerComponent.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  hasIcon: PropTypes.bool,
  iconPosition: PropTypes.string,
  placeholder: PropTypes.string,
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  input: PropTypes.shape({
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({}),
    ]),
    onChange: PropTypes.func,
  }),
  meta: PropTypes.shape({
    active: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    submitError: PropTypes.bool,
    success: PropTypes.bool,
  }),
  showRequiredAsterisk: PropTypes.bool,
  showTimeDropdown: PropTypes.bool,
  theme: PropTypes.shape({
    themeColors: PropTypes.shape({
      orange2: PropTypes.string,
      darkGreen1: PropTypes.string,
    }),
  }),
};

DatePickerComponent.defaultProps = {
  className: "",
  disabled: false,
  hasIcon: true,
  iconPosition: "left",
  placeholder: null,
  isRequired: false,
  label: null,
  input: {
    onChange: () => {},
    value: null,
  },
  meta: {},
  showRequiredAsterisk: false,
  showTimeDropdown: false,
  theme: {
    orange2: "",
    darkGreen1: "",
  },
};
